import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { CoordinatePair } from './types';
import { trpc } from './trpc';
import { getDistanceKm } from './utils';
import z from 'zod';
import { zodAuthedGameSession } from 'jigsaw';
import {
  EventType,
  RecordWithPlaintext,
  requestCreateEvent,
} from '@puzzlehq/sdk';
import { Network } from '@puzzlehq/types';

const N_ROUNDS = 3;
const MAX_PART = 15; // PART
export const SECONDS_PER_ROUND = 60;
const BUFFER_TIME = 60;
const DIFFICULTY_MAX_DISTANCE: number = parseFloat(
  import.meta.env.VITE_DIFFICULTY_MAX_DISTANCE
);
const DIFFICULTY_EXPONENT: number = parseFloat(
  import.meta.env.VITE_DIFFICULTY_EXPONENT
);

export function calculateRoundPART(
  distance: number,
  partPerTurn: number
): number {
  return Math.round(
    Math.max(
      0,
      partPerTurn *
        (1 - Math.pow(distance / DIFFICULTY_MAX_DISTANCE, DIFFICULTY_EXPONENT))
    )
  );
}

export function calculateGamePART(distances: number[], rounds: number): number {
  return distances.reduce((sum, distance) => {
    return sum + calculateRoundPART(distance, MAX_PART / rounds);
  }, 0);
}

export type InitializeStatusSuccess = {
  status: 'success';
  answers: CoordinatePair[];
  distances: number[];
  expiryUnixMs: number;
  round: number;
  rounds: number;
  score: number;
};

export type InitializeStatusError = {
  status: 'error';
  message: string;
};

type InitializeStatus = InitializeStatusError | InitializeStatusSuccess;

type AuthedGameSession = z.infer<typeof zodAuthedGameSession>;

type SubmitGameStatus =
  | {
      status: 'error';
      message: string;
    }
  | {
      status: 'success';
      eventId: string;
    };

type GameState = {
  expiryUnixMs: number | undefined;
  gameStartUnixMs: number | undefined;
  authedGameSession: AuthedGameSession | undefined;
  round: number;
  rounds: number;
  guesses: CoordinatePair[];
  answers: CoordinatePair[];
  distances: number[];
  submitStatus: 'loading' | 'success' | 'error' | undefined;
  submitError: string | undefined;
  submitEventId: string | undefined;
  score: number;
  roundScores: number[];
  isMuted: boolean;
  setIsMuted: (muted: boolean) => void;

  initializeGame: (address: string) => Promise<InitializeStatus>;
  gameIsInitialized: () => boolean;
  storeGuess: (round: number, coordinatePair: CoordinatePair) => void;
  clearGame: () => void;
  submitRound: () => void;
  submitGame: (
    arcadeCoin: RecordWithPlaintext,
    accountAddress: string
  ) => Promise<SubmitGameStatus>;
  incrementRound: () => void;
};

type Consent = {
  message: string;
  signature: string;
  tickets: number;
};

export const useGameStore = create<GameState>()(
  persist(
    (set, get) => ({
      expiryUnixMs: undefined,
      gameStartUnixMs: undefined,
      authedGameSession: undefined,
      round: 0,
      rounds: N_ROUNDS,
      guesses: [],
      answers: [],
      distances: [],
      submitStatus: undefined,
      submitError: undefined,
      submitEventId: undefined,
      score: 0,
      roundScores: [],
      isMuted: false,
      setIsMuted: (muted) => set({ isMuted: muted }),

      initializeGame: async (address: string): Promise<InitializeStatus> => {
        // first see if there is an active game by looking at expiryUnixMs
        // if there is an existing game that's unexpired, use that and return 'success'
        // if the game is expired or nonexistent, start over
        // // ask the server for a new game with X rounds
        // // store the server's response here
        const now = new Date();
        const expiryUnixMs = get().expiryUnixMs;

        console.log({ now, expiryUnixMs });

        const round = get().round;
        const rounds = get().rounds;
        const remainingRounds = rounds - round;
        const timeToExpiry = expiryUnixMs
          ? (expiryUnixMs - now.getTime()) / 1_000
          : undefined;
        const gameTimeRemaining =
          remainingRounds * SECONDS_PER_ROUND + BUFFER_TIME;

        console.log({
          remainingRounds,
          round,
          rounds,
          timeToExpiry,
          expiryUnixMs,
          gameTimeRemaining,
        });

        if (
          remainingRounds > 0 &&
          timeToExpiry &&
          expiryUnixMs &&
          gameTimeRemaining < timeToExpiry
        ) {
          if (get().gameIsInitialized()) {
            console.log('returning existing game!');
            return {
              status: 'success',
              answers: get().answers,
              distances: get().distances,
              expiryUnixMs,
              round: get().round,
              rounds: get().rounds,
              score: get().score,
            };
          }
        }

        try {
          console.log('creating new game');
          get().clearGame();
          let authedGameSession: AuthedGameSession;
          try {
            authedGameSession = await trpc.mysteryCityStart.query({
              rounds: N_ROUNDS,
              network: import.meta.env.VITE_NETWORK! as Network,
              address,
            });
          } catch (e) {
            console.error('Error calling trpc.mysteryCityStart', e);
            const errorStr = typeof e === 'string' ? e : (e as Error).message;
            throw new Error(`Error starting game: ${errorStr}`);
          }

          const answers = authedGameSession.coordinates;
          const expiryUnixMs = new Date(authedGameSession.exp * 1000).getTime();

          set({
            gameStartUnixMs: Date.now(),
            answers,
            rounds: N_ROUNDS,
            expiryUnixMs,
            authedGameSession,
          });

          return {
            status: 'success',
            answers,
            round: 0,
            rounds: N_ROUNDS,
            distances: [],
            expiryUnixMs,
            score: 0,
          };
        } catch (e) {
          return {
            status: 'error',
            message: (e as Error).message,
          };
        }
      },
      gameIsInitialized: () => {
        const { answers, guesses, round, rounds } = get();
        const answersCount = Object.keys(answers).length;
        const guessesCount = Object.keys(guesses).length;

        console.log({ rounds, answersCount, guessesCount });

        if (!rounds || rounds !== answersCount) return false;
        if (round === undefined || round !== guessesCount) return false;

        return true;
      },
      storeGuess: (round, coordinatePair) => {
        const guesses = [...get().guesses];
        guesses[round] = coordinatePair;
        set({ guesses });
      },
      clearGame: () => {
        console.log('clearing game');
        set({
          expiryUnixMs: undefined,
          gameStartUnixMs: undefined,
          authedGameSession: undefined,
          round: 0,
          rounds: N_ROUNDS,
          score: 0,
          roundScores: [],
          guesses: [],
          answers: [],
          distances: [],
          submitStatus: undefined,
          submitError: undefined,
          submitEventId: undefined,
        });
      },
      submitRound: () => {
        // calculate the score
        // if lat and lng are 0, give 0 PART (no user guess)
        // increment the round
        const round = get().round;
        const rounds = get().rounds;
        const score = get().score;

        const guess: CoordinatePair | undefined = get().guesses[round];
        const answer: CoordinatePair | undefined = get().answers[round];

        let roundPART = 0;

        //clear the guess

        if (answer === undefined) {
          throw new Error('Answer is undefined!');
        } else if (guess === undefined) {
          console.warn(
            `Guess for round ${round} is undefined. Verify that's correct.`
          );
        } else if (guess.lat === 0) {
          console.log('no guess that round, setting distance to -1');
          // if the user had no guess that round
          set({
            distances: [...get().distances, -1],
            roundScores: [...get().roundScores, roundPART],
          });
        } else {
          console.log('user had a guess that round, calculating distance');
          const distanceKm = getDistanceKm(
            answer.lat,
            answer.lng,
            guess.lat,
            guess.lng
          );
          roundPART = calculateRoundPART(distanceKm, MAX_PART / rounds);

          set({
            score: roundPART + score,
            distances: [...get().distances, distanceKm],
            roundScores: [...get().roundScores, roundPART],
          });
        }
      },
      incrementRound: () => {
        if (get().round >= 0 && get().round < get().rounds) {
          console.log(`incrementing the round to ${get().round + 1}`);
          set({ round: get().round + 1 });
        }
      },
      submitGame: async (
        arcadeCoin: RecordWithPlaintext,
        accountAddress: string
      ): Promise<SubmitGameStatus> => {
        const submitStatus = get().submitStatus;
        if (submitStatus === 'loading') {
          console.log('game submission already in progress');
          return {
            status: 'error',
            message: 'game submission already in progress',
          };
        }
        // send the coordinate guesses to the server
        // get the number of tickets to mint
        // have the wallet create the event
        console.log('submitGame arcadeCoin', arcadeCoin);
        try {
          set({
            submitStatus: 'loading',
            submitError: undefined,
            submitEventId: undefined,
          });
          const authedGameSession = get().authedGameSession;
          if (!authedGameSession) {
            throw new Error('No authedGameSession!');
          }
          let consent: Consent;

          try {
            consent = await trpc.mysteryCitySubmit.mutate({
              authedSession: authedGameSession,
              guesses: Object.values(get().guesses),
              network: import.meta.env.VITE_NETWORK! as Network,
              address: accountAddress,
            });
          } catch (e) {
            console.error('Error calling trpc.mysteryCitySubmit', e);
            const errorStr = typeof e === 'string' ? e : (e as Error).message;
            throw new Error(`Error submitting game: ${errorStr}`);
          }

          console.log('consent', consent);

          const inputs = [
            arcadeCoin.plaintext,
            consent.message,
            consent.signature,
          ];

          console.log(
            'calling requestCreateEvent with inputs: ',
            JSON.stringify(inputs)
          );
          const response = await requestCreateEvent({
            fee: 0.022731,
            functionId: 'submit_game',
            programId: import.meta.env.VITE_MYSTERY_CITY_CONTRACT!,
            type: EventType.MysteryCity,
            inputs,
            address: accountAddress,
            network: import.meta.env.VITE_NETWORK,
            tokenIds: ['3126633106field', '4255871388field'],
          });
          if (!response.eventId || response.error) {
            set({
              submitError: response.error ?? 'Could not create event',
              submitStatus: 'error',
            });
            return {
              status: 'error',
              message: response.error ?? 'Could not create event',
            };
          }
          set({
            submitError: undefined,
            submitEventId: response.eventId,
            submitStatus: 'success',
          });
          return {
            status: 'success',
            eventId: response.eventId,
          };
        } catch (e) {
          console.error('Error in submitGame', e);
          set({ submitStatus: 'error', submitError: (e as Error).message });
          return {
            status: 'error',
            message: (e as Error).message,
          };
        }
      },
    }),
    {
      name: 'game-storage',
    }
  )
);
